import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Instagram, Mail, MessageCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { to: "/", label: "Início" },
    { to: "/sobre", label: "Sobre" },
    { to: "/#metodo", label: "Método" },
    { to: "/conteudos", label: "Conteúdos" },
    { to: "/#depoimentos", label: "Depoimentos" },
    { to: "/contato", label: "Contato" },
  ];

  const handleNav = (item, closeMobile = false) => (e) => {
    if (item.to.startsWith("/#")) {
      e.preventDefault();
      const id = item.to.split("#")[1];
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      if (closeMobile) setOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(251,248,243,0.92)" : "rgba(251,248,243,0.6)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: scrolled ? "1px solid var(--border-light)" : "1px solid transparent",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_ceo-transformacao/artifacts/30ukavcr_Design%20sem%20nome%20%283%29.png"
              alt="Logo"
              className="h-10 w-10 lg:h-11 lg:w-11 object-contain rounded-lg flex-shrink-0"
            />
            <span className="brand-logo text-xl lg:text-2xl tracking-wide">
              <span className="ana">Ana Paula</span>{" "}
              <span className="m">Moraes</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {navItems.map((item) => (
              <a
                key={item.to + item.label}
                href={item.to}
                onClick={handleNav(item)}
                className="text-[13px] font-medium transition-colors duration-200 hover:text-[color:var(--copper)] relative group"
                style={{ color: "var(--text-primary)" }}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
                <span className="absolute left-0 -bottom-1 h-px w-0 bg-[color:var(--copper)] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/membros"} className="btn-primary text-sm" data-testid="header-area-membros">
                {user.role === "admin" ? "Painel Admin" : "Área de Membros"}
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[13px] font-medium hover:text-[color:var(--copper)] transition-colors" style={{ color: "var(--text-primary)" }} data-testid="header-login-link">
                  Entrar
                </Link>
                <Link to="/login" className="btn-primary text-sm" data-testid="header-area-membros">
                  Área de Membros
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            style={{ color: "var(--text-primary)" }}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${open ? "max-h-[600px] border-t" : "max-h-0"}`}
          style={{ borderColor: "var(--border-light)", background: "var(--bg)" }}
        >
          <nav className="px-6 py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <a
                key={"m-" + item.to + item.label}
                href={item.to}
                onClick={(e) => {
                  if (item.to.startsWith("/#")) handleNav(item, true)(e);
                  else setOpen(false);
                }}
                className="py-3 text-base font-medium border-b"
                style={{ color: "var(--text-primary)", borderColor: "var(--border-light)" }}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to={user ? (user.role === "admin" ? "/admin" : "/membros") : "/login"}
              onClick={() => setOpen(false)}
              className="btn-primary text-sm mt-5 w-full"
              data-testid="mobile-area-membros"
            >
              {user ? (user.role === "admin" ? "Painel Admin" : "Área de Membros") : "Entrar na Área de Membros"}
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <Footer />
    </div>
  );
}

function Footer() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})); }, []);

  return (
    <footer className="pt-24 pb-12" style={{ borderTop: "1px solid var(--border-light)", background: "var(--bg)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="brand-logo text-2xl mb-4">
              <span className="ana">Ana Paula</span> <span className="m">Moraes</span>
            </div>
            <p className="font-display italic text-xl leading-snug mb-5" style={{ color: "var(--copper)" }}>
              Eu sou a CEO da minha vida.
            </p>
            <p className="text-sm leading-relaxed max-w-sm" style={{ color: "var(--text-secondary)" }}>
              Um espaço para mulheres que decidiram parar de adiar a própria vida e começar a vivê-la com clareza, identidade e propósito.
            </p>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-[10px] uppercase tracking-[0.32em] font-semibold mb-5" style={{ color: "var(--copper)" }}>Navegação</h4>
            <ul className="space-y-3 text-sm" style={{ color: "var(--text-secondary)" }}>
              <li><Link to="/sobre" className="hover:text-[color:var(--copper)] transition-colors">Sobre</Link></li>
              <li><Link to="/conteudos" className="hover:text-[color:var(--copper)] transition-colors">Conteúdos</Link></li>
              <li><Link to="/contato" className="hover:text-[color:var(--copper)] transition-colors">Contato</Link></li>
              <li><Link to="/login" className="hover:text-[color:var(--copper)] transition-colors">Área de Membros</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="text-[10px] uppercase tracking-[0.32em] font-semibold mb-5" style={{ color: "var(--copper)" }}>Conecte-se</h4>
            <div className="flex flex-col gap-3 text-sm" style={{ color: "var(--text-secondary)" }}>
              {s.instagram && (
                <a href={s.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 group" data-testid="footer-instagram">
                  <Instagram size={16} className="group-hover:text-[color:var(--copper)] transition-colors" />
                  <span className="group-hover:text-[color:var(--copper)] transition-colors">@eusouanapaulam</span>
                </a>
              )}
              {s.whatsapp && (
                <a href={`https://wa.me/${s.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 group" data-testid="footer-whatsapp">
                  <MessageCircle size={16} className="group-hover:text-[color:var(--copper)] transition-colors" />
                  <span className="group-hover:text-[color:var(--copper)] transition-colors">{s.whatsapp}</span>
                </a>
              )}
              {s.email && (
                <a href={`mailto:${s.email}`} className="flex items-center gap-3 group" data-testid="footer-email">
                  <Mail size={16} className="group-hover:text-[color:var(--copper)] transition-colors" />
                  <span className="group-hover:text-[color:var(--copper)] transition-colors">{s.email}</span>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs" style={{ borderTop: "1px solid var(--border-light)", color: "var(--text-muted)" }}>
          <span>© {new Date().getFullYear()} Ana Paula Moraes — Todos os direitos reservados.</span>
          <span className="font-display italic" style={{ color: "var(--copper)" }}>Marca pessoal · Liderança feminina</span>
        </div>
      </div>
    </footer>
  );
}
